import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Cookies from "universal-cookie";
import queryString from "query-string";
import Alert from "@mui/material/Alert";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { get, post } from "../services/api";
import { groupBy } from "../common/util";
import { useToast } from "../admin/components/ToastProvider";
import {
  EmployeeEmptyState,
  EmployeeErrorState,
  EmployeeLoadingState,
  EmployeePageHeader,
  EmployeeSurface,
} from "../employee/components/EmployeePage";
import { tokens as t } from "../theme/tokens";

type UploadData = {
  fileName: string;
  file: File | null;
  emp_doc_relation_id: number;
  createdAt?: Date;
};

type DocumentGroups = Record<string, any[]>;

const emptyUpload: UploadData = {
  fileName: "",
  file: null,
  emp_doc_relation_id: 0,
};

const acceptedFileTypes = [
  "application/pdf",
  "image/jpeg",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/heic",
].join(",");

const documentStatus = (item: any) => {
  // Read-only documents are never signed, so is_signed stays 0 for good.
  if (item.document_type === 2) return { label: "For reference", color: "default" as const };
  if (item.doc_status === 5) return { label: "Needs attention", color: "error" as const };
  if (item.doc_status === 2) return { label: "Processing", color: "warning" as const };
  if (item.is_signed) return { label: "Complete", color: "success" as const };
  return { label: "To do", color: "default" as const };
};

const DocumentRow: React.FC<{
  item: any;
  instruction: string;
  action: React.ReactNode;
}> = ({ item, instruction, action }) => {
  const status = documentStatus(item);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) auto" },
        alignItems: { xs: "stretch", sm: "center" },
        gap: 2,
        px: { xs: 2, sm: 2.5 },
        py: 2,
        borderTop: `1px solid ${t.color.rowLine}`,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 600 }}>{item.document_name}</Typography>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {instruction}
          </Typography>
          <Chip label={status.label} color={status.color} size="small" variant={status.color === "default" ? "outlined" : "filled"} />
        </Stack>
      </Box>
      <Box sx={{ display: "flex", justifyContent: { xs: "stretch", sm: "flex-end" }, "& > *": { flex: { xs: 1, sm: "initial" } } }}>
        {action}
      </Box>
    </Box>
  );
};

const DocumentSection: React.FC<{
  title: string;
  description: string;
  children: React.ReactNode;
}> = ({ title, description, children }) => (
  <EmployeeSurface>
    <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2.25 }}>
      <Typography component="h2" variant="h6">
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {description}
      </Typography>
    </Box>
    {children}
  </EmployeeSurface>
);

const EmpDocumentDashboard: React.FC = () => {
  const { id: employeeId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const parsedToken = queryString.parse(window.location.search).access_token;
  const publicToken = typeof parsedToken === "string" ? parsedToken : localStorage.getItem("publicToken") || "";

  const [employeeDetails, setEmployeeDetails] = useState<any>({});
  const [designationDocs, setDesignationDocs] = useState<any[]>([]);
  const [documents, setDocuments] = useState<DocumentGroups>({});
  const [loadingEmployee, setLoadingEmployee] = useState(true);
  const [loadingDocuments, setLoadingDocuments] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadData, setUploadData] = useState<UploadData>(emptyUpload);
  const [afterSignOpen, setAfterSignOpen] = useState(new URLSearchParams(window.location.search).has("ref"));

  useEffect(() => {
    if (publicToken) localStorage.setItem("publicToken", publicToken);
    const cookieName = process.env.REACT_APP_PUBLIC_COOKIE_NAME || "";
    if (cookieName && publicToken) {
      new Cookies().set(cookieName, publicToken, { domain: ".paperlessnow.ca", path: "/organization-documents" });
    }
  }, [publicToken]);

  useEffect(() => {
    document.title = "PaperlessNOW : Employee Documents";
    if (!employeeId || !publicToken) {
      setError("This employee document link is incomplete or has expired. Please contact your administrator for a fresh link.");
      setLoadingEmployee(false);
      setLoadingDocuments(false);
      return;
    }

    setLoadingEmployee(true);
    get(`openEmployee/${employeeId}`, publicToken)
      .then((response: any) => {
        const employee = response?.payload?.data || {};
        setEmployeeDetails(employee);
        return get(`master/documents/${employee.emp_designation}`, publicToken);
      })
      .then((response: any) => setDesignationDocs(response?.payload?.data || []))
      .catch(() => setError("We could not load your documents. The link may have expired; please contact your administrator."))
      .finally(() => setLoadingEmployee(false));
  }, [employeeId, publicToken]);

  const fetchDocuments = useCallback(() => {
    if (!employeeId || !publicToken) return Promise.resolve();

    setLoadingDocuments(true);
    return get(`employee/documents/open/${employeeId}`, publicToken)
      .then((response: any) => {
        const allowedIds = designationDocs.map((item) => item.doc_id);
        const allDocuments = response?.payload?.data || [];
        const relevantDocuments = allDocuments.filter((item: any) => allowedIds.includes(item.doc_id));
        setDocuments(groupBy(relevantDocuments, "document_type") || {});
      })
      .catch(() => setError("We could not refresh your document list. Please try the link again."))
      .finally(() => setLoadingDocuments(false));
  }, [designationDocs, employeeId, publicToken]);

  useEffect(() => {
    if (!loadingEmployee) fetchDocuments();
  }, [fetchDocuments, loadingEmployee]);

  useEffect(() => {
    if (!employeeId || !publicToken) return;
    get(`employee/checkAllDocSignedOpen/${employeeId}`, publicToken).then((response: any) => {
      if (response?.payload?.data?.[0]?.unsignedDoc === 0) {
        get(`employee/updateAllDocSignedOpen/${employeeId}`, publicToken);
      }
    }).catch(() => undefined);
  }, [employeeId, publicToken]);

  const processingCount = useMemo(
    () => Object.values(documents).flat().filter((item: any) => item.doc_status === 2).length,
    [documents]
  );

  useEffect(() => {
    if (!processingCount) return;
    const interval = window.setInterval(fetchDocuments, 3000);
    return () => window.clearInterval(interval);
  }, [fetchDocuments, processingCount]);

  const signDocuments = documents["1"] || [];
  const readDocuments = documents["2"] || [];
  const uploadDocuments = documents["3"] || [];
  const signedCount = signDocuments.filter((item) => item.is_signed).length;
  const completion = signDocuments.length ? Math.round((signedCount / signDocuments.length) * 100) : 0;

  const openDocument = (url: string) => window.open(url, "_blank", "noopener,noreferrer");

  const signDocument = (item: any) => {
    localStorage.setItem("signDocId", item.emp_doc_relation_id);
    navigate(`/employee/view-document/${employeeId}`);
  };

  const startUpload = (item: any) => {
    setUploadData({ ...emptyUpload, emp_doc_relation_id: item.emp_doc_relation_id });
    setUploadOpen(true);
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size >= 10 * 1024 * 1024) {
      event.target.value = "";
      setUploadData((current) => ({ ...current, fileName: "", file: null }));
      showToast("File too large. Please choose a file smaller than 10 MB.", "error");
      return;
    }
    setUploadData((current) => ({ ...current, fileName: file.name, file, createdAt: new Date() }));
  };

  const uploadDocument = () => {
    if (!uploadData.file || !publicToken) return;
    const payload: Omit<UploadData, "file"> = {
      fileName: uploadData.fileName,
      emp_doc_relation_id: uploadData.emp_doc_relation_id,
      createdAt: uploadData.createdAt,
    };
    const file = uploadData.file;

    setUploadOpen(false);
    setUploading(true);
    post("employee/uploaddocument", payload, publicToken)
      .then((response: any) =>
        axios.put(response.payload.body.preSignedUrl, file, { headers: { "Content-Type": "" } }).then(() => response)
      )
      .then((response: any) => {
        setDocuments((current) => ({
          ...current,
          "3": (current["3"] || []).map((item: any) =>
            item.emp_doc_relation_id === uploadData.emp_doc_relation_id
              ? { ...item, is_signed: 1, pdf_path: response.payload.body.pdf_path, doc_status: 2 }
              : item
          ),
        }));
        showToast("Document uploaded successfully. Verification is now in progress.");
      })
      .catch(() => showToast("We could not upload that document. Please try again.", "error"))
      .finally(() => setUploading(false));
  };

  if (loadingEmployee) return <EmployeeLoadingState message="Loading your employee record…" />;

  return (
    <>
      <EmployeePageHeader
        eyebrow="Employee onboarding"
        title={employeeDetails.first_name ? `${employeeDetails.first_name} ${employeeDetails.last_name || ""}` : "Your documents"}
        description="Review, sign, and upload the documents required for your onboarding."
      />

      {error && <EmployeeErrorState message={error} />}

      {!error && (
        <Stack spacing={3}>
          <EmployeeSurface>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(280px, 0.75fr)" },
                gap: { xs: 2.5, md: 4 },
                p: { xs: 2.5, sm: 3 },
              }}
            >
              <Box>
                <Typography variant="overline" color="text.secondary">Workplace</Typography>
                <Typography variant="h5" sx={{ mt: 0.25 }}>
                  {employeeDetails.store || "Store information unavailable"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                  Complete each item below. Documents being verified will update automatically.
                </Typography>
              </Box>
              <Box sx={{ bgcolor: t.color.mint, border: `1px solid ${t.color.mintLine}`, borderRadius: `${t.radius.control}px`, p: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 1 }}>
                  <Typography sx={{ fontWeight: 600 }}>Signature progress</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: t.color.data }}>
                    {signedCount} of {signDocuments.length} signed
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={completion}
                  aria-label={`${completion}% of signature documents complete`}
                  sx={{ height: 8, borderRadius: 4, bgcolor: t.color.paper, "& .MuiLinearProgress-bar": { borderRadius: 4 } }}
                />
              </Box>
            </Box>
          </EmployeeSurface>

          {loadingDocuments ? (
            <EmployeeLoadingState message="Loading your document checklist…" />
          ) : (
            <>
              {readDocuments.length > 0 && (
                <DocumentSection title="Read" description="Reference material to review before your orientation.">
                  {readDocuments.map((item: any) => (
                    <DocumentRow
                      key={item.emp_doc_relation_id}
                      item={item}
                      instruction="Download and review"
                      action={<Button variant="outlined" onClick={() => openDocument(item.full_path)}>Download</Button>}
                    />
                  ))}
                </DocumentSection>
              )}

              {signDocuments.length > 0 && (
                <DocumentSection title="Review and sign" description="Open each document, complete the required fields, and add your signature.">
                  {signDocuments.map((item: any) => (
                    <DocumentRow
                      key={item.emp_doc_relation_id}
                      item={item}
                      instruction={item.is_signed ? "Your signed copy is ready" : item.doc_status === 2 ? "Your signature is being processed" : "Signature required"}
                      action={
                        item.is_signed && item.pdf_path ? (
                          <Button variant="outlined" onClick={() => openDocument(item.pdf_path)}>View signed copy</Button>
                        ) : item.doc_status === 1 ? (
                          <Button variant="contained" onClick={() => signDocument(item)}>See &amp; sign</Button>
                        ) : (
                          <Button variant="outlined" disabled>Processing</Button>
                        )
                      }
                    />
                  ))}
                </DocumentSection>
              )}

              {uploadDocuments.length > 0 && (
                <DocumentSection title="Upload" description="Provide payroll, certification, or work-authorization documents requested by your employer.">
                  {uploadDocuments.map((item: any) => (
                    <DocumentRow
                      key={item.emp_doc_relation_id}
                      item={item}
                      instruction={item.doc_status === 5 ? "Verification failed—please choose a clearer copy" : item.doc_status === 2 ? "Verification in progress" : item.is_signed ? "Uploaded copy" : "Upload required"}
                      action={
                        item.is_signed && item.pdf_path && item.doc_status !== 5 ? (
                          <Button variant="outlined" onClick={() => openDocument(item.pdf_path)}>View</Button>
                        ) : item.doc_status === 2 ? (
                          <Button variant="outlined" disabled>Processing</Button>
                        ) : (
                          <Button variant="contained" onClick={() => startUpload(item)}>{item.doc_status === 5 ? "Re-upload" : "Upload"}</Button>
                        )
                      }
                    />
                  ))}
                </DocumentSection>
              )}

              {!readDocuments.length && !signDocuments.length && !uploadDocuments.length && (
                <EmployeeSurface>
                  <EmployeeEmptyState title="No documents are assigned" description="There is nothing for you to complete right now." />
                </EmployeeSurface>
              )}
            </>
          )}

          <Alert severity="info" variant="outlined" sx={{ bgcolor: t.color.paper }}>
            Need technical help? Email <a href="mailto:hello@paperlessnow.ca">hello@paperlessnow.ca</a>.
          </Alert>
        </Stack>
      )}

      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} fullWidth maxWidth="sm" aria-labelledby="upload-document-title">
        <DialogTitle id="upload-document-title">Upload document</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            Choose a PDF, JPEG, HEIC, DOC, or DOCX file smaller than 10 MB.
          </DialogContentText>
          <TextField
            type="file"
            label="Document file"
            InputLabelProps={{ shrink: true }}
            inputProps={{ accept: acceptedFileTypes }}
            onChange={onFileChange}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setUploadOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!uploadData.fileName} onClick={uploadDocument}>Upload</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={afterSignOpen} onClose={() => setAfterSignOpen(false)} fullWidth maxWidth="xs" aria-labelledby="signature-saved-title">
        <DialogTitle id="signature-saved-title">Signature saved</DialogTitle>
        <DialogContent>
          <DialogContentText>Your signature is being processed. You can continue with the remaining documents.</DialogContentText>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="contained" onClick={() => setAfterSignOpen(false)} autoFocus>Continue</Button>
        </DialogActions>
      </Dialog>

      <Backdrop open={uploading} sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 1 }}>
        <Box role="status" aria-live="polite" sx={{ textAlign: "center" }}>
          <CircularProgress color="inherit" />
          <Typography sx={{ mt: 1 }}>Uploading document…</Typography>
        </Box>
      </Backdrop>
    </>
  );
};

export default EmpDocumentDashboard;
